import React, { useEffect, useState } from 'react';
import {
    Box, Button, Tooltip, IconButton, MenuItem, TextField, Typography, Alert, Divider, FormControlLabel, Checkbox
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import { useParams, useOutletContext } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAppSnackbar } from '../contexts/AppSnackbarContext';
import { useAppConfirm } from '../contexts/AppConfirmContext';

export default function Events() {
    const { selectedConnection, setSelectedConnection, setRefreshTree } = useOutletContext();
    
    const { conId, eventId } = useParams();
    //const { events, setEvents } = useOutletContext();
    const [isLoading, setIsLoading] = useState(false);
    const [currentEvent, setCurrentEvent] = useState(null);
    const { showSnackbar } = useAppSnackbar();
    const [isEditMode, setIsEditMode] = useState(eventId === 'new'); 
    const navigate = useNavigate();
    const { confirm } = useAppConfirm();
    const [form, setForm] = useState({
        connectionId: '',
        name: '',
        description: '',
        config: {
            method: 'POST',
            QueryParams: [],
            BodyParams: [],
            SubscriptionParams: [], 
            SubscribeURL: '',
            PayloadType: 'object'
        }
    });

    const [subscriptionParamsText, setSubscriptionParamsText] = useState('');
    const [subscriptionParamsError, setSubscriptionParamsError] = useState('');

    const DataTypes = [
        'String',
        'Number',
        'Boolean',
        'Object',
        'Array',
        'Date'
    ];
    
    // Fetch event if editing
    useEffect(() => {
        let isMounted = true;
        //console.log(eventId);
        async function fetchData() {
            setIsLoading(true);

            // Fetch connection if needed
            //let conn = selectedConnection;
            //if (!conn) {
            //    const resp = await fetch(`/api/connection/${conId}`, { credentials: 'include' });
            //    conn = await resp.json();
            //    if (isMounted) setSelectedConnection(conn);
            //}
            const resp = await fetch(`/api/connection/${conId}`, { credentials: 'include' });
            let conn = await resp.json();
            if (isMounted) setSelectedConnection(conn);

            // Fetch event if editing
            if (eventId && eventId !== 'new') {
                setIsEditMode(false);
                const encodedEventId = encodeURIComponent(eventId);
                const res = await fetch(`/api/Events/gt/${encodedEventId}`, { credentials: 'include' });
                if (!res.ok) {
                    setIsLoading(false);
                    return;
                }
                const data = await res.json();
                let configObj = {
                    method: 'POST',
                    QueryParams: [],
                    BodyParams: [],
                    SubscriptionParams: [],
                    SubscribeURL: '',
                    PayloadType: 'object',
                    UseConnectionAuth: true,
                    UseSigninKey: true
                };
                if (data.config) {
                    try {
                        configObj = typeof data.config === 'string'
                            ? JSON.parse(data.config)
                            : data.config;
                    } catch { }
                }
                if (isMounted) {
                    setCurrentEvent(data);
                    setForm({
                        connectionId: data.connectionId || '',
                        name: data.name || '',
                        description: data.description || '',
                        subscriptionId: data.subscriptionId || '',
                        config: {
                            method: configObj.method || 'POST',
                            QueryParams: Array.isArray(configObj.QueryParams) ? configObj.QueryParams : [],
                            BodyParams: Array.isArray(configObj.BodyParams) ? configObj.BodyParams : [],
                            SubscriptionParams: configObj.SubscriptionParams ? configObj.SubscriptionParams : [],
                            SubscribeURL: configObj.SubscribeURL || '',
                            PayloadType: configObj.PayloadType || 'object',
                            UseConnectionAuth: configObj.UseConnectionAuth ?? true,
                            UseSigninKey: configObj.UseSigninKey ?? true,
                            EndpointName: 'endpoint'
                        }
                    });
                    setSubscriptionParamsText(
                        configObj.SubscriptionParams
                            ? JSON.stringify(configObj.SubscriptionParams, null, 2)
                            : JSON.stringify({
                                "resource": "Loan",
                                "events": [
                                    "document"
                                ],
                                "description": "Subscribe to document created events for workflow listeners"
                            }, null, 2)
                    );
                }
            } else {
                setIsEditMode(true);
                if (isMounted) {
                    setForm({
                        connectionId: '',
                        name: '',
                        description: '',
                        subscriptionId: '',
                        config: {
                            method: 'POST',
                            QueryParams: [],
                            BodyParams: [],
                            SubscriptionParams: [],
                            SubscribeURL: '',
                            PayloadType: 'object',
                            UseConnectionAuth: true,
                            UseSigninKey: true,
                            EndpointName: 'endpoint'
                        }
                    });
                    setSubscriptionParamsText(JSON.stringify({
                        "resource": "Loan",
                        "events": [
                            "document"
                        ],
                        "description": "Subscribe to document created events for workflow listeners"
                    }, null, 2));
                }
            }
            if (isMounted) setIsLoading(false);
        }
        fetchData();
        return () => { isMounted = false; };
    }, [conId, eventId]);

    const handleEditClick = () => setIsEditMode(true);

    const handleDeleteEvent = async () => {
        const confirmed = await confirm({
            title: 'Delete Event',
            content: 'Are you sure you want to Delete? This process will be permanent',
            confirmText: 'Accept',
            cancelText: 'Cancel'
        });
        if (confirmed) {
            try {
                const res = await fetch(`/api/events/${eventId}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    //body: JSON.stringify(payload)
                });
                if (res.ok) {
                    showSnackbar('Event deleted successfully!', 'success');
                    setRefreshTree(prev => prev + 1);
                    navigate('/connections', { replace: true });
                } else if (res.status === 409) {
                    const body = await res.text();
                    showSnackbar(body, 'error');
                }
            } catch (e) {
                showSnackbar('An error occurred while deleting the event. Please try again later.', 'error');
            }
        }
    }

    const handleCancelEdition = async () => {
        if (currentEvent !== null) {
            if (isFormChanged()) {
                const confirmed = await confirm({
                    title: 'Cancel Changes',
                    content: 'Are you sure you want to cancel? Unsaved changes will be lost.',
                    confirmText: 'Confirm',
                    cancelText: 'Cancel'
                });
                if (confirmed) {
                    setIsEditMode(false);
                    let configObj = typeof currentEvent.config === 'string'
                        ? JSON.parse(currentEvent.config)
                        : currentEvent.config;
                    setForm({
                        connectionId: currentEvent.connectionId || '',
                        name: currentEvent.name || '',
                        description: currentEvent.description || '',
                        config: {
                            method: configObj.method || 'POST',
                            QueryParams: Array.isArray(configObj.QueryParams) ? configObj.QueryParams : [],
                            BodyParams: Array.isArray(configObj.BodyParams) ? configObj.BodyParams : [],
                            SubscriptionParams: Array.isArray(configObj.SubscriptionParams) ? configObj.SubscriptionParams : [],
                            SubscribeURL: configObj.SubscribeURL || '',
                            PayloadType: configObj.PayloadType || 'object'
                        }
                    });
                }
            } else {
                setIsEditMode(false);
            }
        }
    }

    // Helper to update config in form state
    const updateConfig = (key, value) => {
        setForm(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [key]: value
            }
        }));
    };

    //useEffect(() => {
    //    fetch('/api/Events', { credentials: 'include' })
    //        .then(res => res.json())
    //        .then(setEvents);
    //}, []);

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === 'method' || name === 'SubscribeURL' || name === 'PayloadType' || name === 'EndpointName') {
            updateConfig(name, value);
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    // Param handlers for array of single-key objects
    const handleParamKeyChange = (type, idx, newKey) => {
        const params = [...form.config[type]];
        const oldObj = params[idx];
        const oldKey = Object.keys(oldObj)[0];
        const value = oldObj[oldKey];
        if (newKey !== oldKey) {
            params[idx] = { [newKey]: value };
            updateConfig(type, params);
        }
    };

    const handleParamValueChange = (type, idx, newValue) => {
        const params = [...form.config[type]];
        const oldObj = params[idx];
        const key = Object.keys(oldObj)[0];
        params[idx] = { [key]: newValue };
        updateConfig(type, params);
    };

    const handleAddParam = (type) => {
        updateConfig(type, [...form.config[type], { "": "" }]);
    };

    const handleRemoveParam = (type, idx) => {
        const params = [...form.config[type]];
        params.splice(idx, 1);
        updateConfig(type, params);
    };

    const handleSubscriptionParamKeyChange = (idx, newKey) => {
        const params = [...form.config.SubscriptionParams];
        const oldObj = params[idx];
        const oldKey = Object.keys(oldObj)[0];
        const value = oldObj[oldKey];
        if (newKey !== oldKey) {
            params[idx] = { [newKey]: value };
            updateConfig('SubscriptionParams', params);
        }
    };

    const handleSubscriptionParamValueChange = (idx, newValue) => {
        const params = [...form.config.SubscriptionParams];
        const oldObj = params[idx];
        const key = Object.keys(oldObj)[0];
        params[idx] = { [key]: newValue };
        updateConfig('SubscriptionParams', params);
    };

    const handleAddSubscriptionParam = () => {
        updateConfig('SubscriptionParams', [...form.config.SubscriptionParams, { "": "" }]);
    };

    const handleRemoveSubscriptionParam = (idx) => {
        const params = [...form.config.SubscriptionParams];
        params.splice(idx, 1);
        updateConfig('SubscriptionParams', params);
    };

    function normalizeConfig(config) {
        return {
            method: config.method || 'POST',
            QueryParams: Array.isArray(config.QueryParams) ? config.QueryParams : [],
            BodyParams: Array.isArray(config.BodyParams) ? config.BodyParams : [],
            SubscriptionParams: config.SubscriptionParams ? config.SubscriptionParams : [],
            SubscribeURL: config.SubscribeURL || '',
            PayloadType: config.PayloadType || 'object'
        };
    }

    function isFormChanged() {
        if (!currentEvent) return true;
        const originalConfig = typeof currentEvent.config === 'string'
            ? normalizeConfig(JSON.parse(currentEvent.config))
            : normalizeConfig(currentEvent.config);
        const currentConfig = normalizeConfig(form.config);
        const original = {
            connectionId: currentEvent.connectionId,
            name: currentEvent.name,
            description: currentEvent.description,
            config: originalConfig
        };
        const current = {
            connectionId: form.connectionId,
            name: form.name,
            description: form.description,
            config: currentConfig
        };
        return JSON.stringify(original) !== JSON.stringify(current);
    }

    const handleSubmit = async e => {
        let isUpdate = eventId && eventId !== 'new';
        e.preventDefault();
        if (!isFormChanged()) {
            showSnackbar('No changes to save!', 'warning');
            return;
        }
        // Store config as stringified JSON
        const payload = {
            connectionId: conId,
            name: form.name,
            description: form.description,
            config: JSON.stringify(form.config)
        };
        const res = await fetch(isUpdate ? `/api/Events/${eventId}` : '/api/Events', {
            method: isUpdate ? 'PATCH' : 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload)
        });
        if (res.ok) {
            const newEvent = await res.json();
            showSnackbar('Event saved successfully!', 'success');
            setRefreshTree(prev => prev + 1);
            setIsEditMode(false);
            navigate(`/connections/${conId}/events/${newEvent.id}`, {replace: true});
            //navigate('/connections', { replace: true });
        }
    };

    const handleSubscribeWebhook = async () => {
        try {
            const url = form.config.SubscribeURL;
            let params = form.config.SubscriptionParams;
            if (!eventId || eventId === 'new') {
                showSnackbar('You must save the event before subscribing.', 'warning');
                return;
            }
            const res = await fetch(`/api/events/${eventId}/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    SubscribeURL: url,
                    SubscriptionParams: params
                })
            });
            if (res.ok) {
                showSnackbar('Webhook subscription sent!', 'success');
                let resp = await res.json();
                setForm({ ...form, suscriptionId: resp.suscriptionId });
            } else {
                const error = await res.text();
                showSnackbar(error || 'Failed to subscribe to webhook.', 'error');
            }
        } catch (e) {
            showSnackbar('Error subscribing to webhook: ' + e.message, 'error');
        }
    };

    const handleDeleteSubscription = async () => {
        if (!eventId || eventId === 'new') {
            showSnackbar('You must save the event before deleting a subscription.', 'warning');
            return;
        }
        try {
            const res = await fetch(`/api/events/${eventId}/subscribe`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
            });
            if (res.ok) {
                showSnackbar('Webhook subscription deleted!', 'success');
                setForm(prev => ({ ...prev, subscriptionId: '' }));
            } else {
                const error = await res.text();
                showSnackbar(error || 'Failed to delete webhook subscription.', 'error');
            }
        } catch (e) {
            showSnackbar('Error deleting webhook subscription: ' + e.message, 'error');
        }
    };

    const handleSubscriptionParamsTextChange = (e) => {
        const value = e.target.value;
        setSubscriptionParamsText(value);
        if (value.trim() === '') {
            setSubscriptionParamsError('');
            updateConfig('SubscriptionParams', {});
            return;
        }
        try {
            const parsed = JSON.parse(value);
            if (typeof parsed !== 'object' || parsed === null) {
                setSubscriptionParamsError('Must be a valid JSON object or array.');
                updateConfig('SubscriptionParams', {});
            } else {
                setSubscriptionParamsError('');
                updateConfig('SubscriptionParams', parsed);
            }
        } catch {
            setSubscriptionParamsError('Invalid JSON format.');
            updateConfig('SubscriptionParams', {});
        }
    };

    // Which param type to show
    const paramType = form.config.method === 'POST' ? 'BodyParams' : 'QueryParams';
    const paramsArr = form.config[paramType];
    // Determine the eventId to show in the URL
    const urlEventId = eventId || 'new';
    const isNew = urlEventId === 'new';

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" flexDirection="column" mb={2}>
               
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h7" ml={1}>{isNew ? 'New Event' : (currentEvent?.name ?? '')}</Typography>
                    <Box display="flex" alignItems="center">
                        {!isEditMode && (
                            <Tooltip title="Edit">
                                <IconButton onClick={handleEditClick} color="primary">
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isEditMode && (
                            <>
                                <Box display="flex" gap={1} flexDirection="row">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        startIcon={<SaveIcon />}
                                        disabled={isLoading}
                                    >
                                        {eventId === 'new' ? 'Create Event' : 'Save Changes'}
                                    </Button>
                                    {eventId !== 'new' && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={handleDeleteEvent}
                                            disabled={isLoading}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    {eventId !== 'new' && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancelEdition}
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
                <Divider sx={{ p: 2 }}></Divider>
                <Typography variant="subtitle1" color="textSecondary" sx={{ ml: 4, mt: 1 }}>
                    Endpoint URL: <b>api/Events/{isNew ? '{new-event-id-guid}' : urlEventId}</b>
                </Typography>
            </Box>
            {isLoading && (<CircularProgress />)}
            {!isLoading && (
                <>
                    <TextField
                        margin="dense"
                        label="Name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant={ isEditMode ? 'outlined': 'filled'}
                        InputProps={{ readOnly: !isEditMode }}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant={isEditMode ? 'outlined': 'filled'}
                        InputProps={{ readOnly: !isEditMode }}
                    />
                    {([1, 4].includes(selectedConnection?.type)) && (
                        <>
                            <TextField
                                margin="dense"
                                label="Subscribe Path"
                                name="SubscribeURL"
                                value={form.config.SubscribeURL}
                                onChange={handleChange}
                                fullWidth
                                variant={isEditMode ? 'outlined' : 'filled'}
                                InputProps={{ readOnly: !isEditMode }}
                                helperText="This is the path appended to the base URL from the connection configuration."
                            />
                            <TextField
                                margin="dense"
                                label="Endpoint Name"
                                name="EndpointName"
                                value={form.config.EndpointName}
                                onChange={handleChange}
                                fullWidth
                                variant={isEditMode ? 'outlined' : 'filled'}
                                InputProps={{ readOnly: !isEditMode }}
                                helperText="This name will be used to refer to your API endpoint when sending requests from webhook."
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.config.UseConnectionAuth}
                                        onChange={e => updateConfig('UseConnectionAuth', e.target.checked)}
                                        disabled={!isEditMode}
                                    />
                                }
                                label="Need authentication for subscription (uses Connection auth config)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.config.UseSigninKey}
                                        onChange={e => updateConfig('UseSigninKey', e.target.checked)}
                                        disabled={!isEditMode}
                                    />
                                }
                                label="Use sign-in key for subscription (prevent unauthorized sources to the listener endpoint)"
                            />
                            <Box mt={2}>
                                <Typography variant="subtitle1">
                                    Subscription Params (JSON object or array)
                                </Typography>
                                <TextField
                                    label="Subscription Params"
                                    value={subscriptionParamsText}
                                    onChange={handleSubscriptionParamsTextChange}
                                    fullWidth
                                    multiline
                                    minRows={6}
                                    variant={isEditMode ? 'outlined' : 'filled'}
                                    InputProps={{ readOnly: !isEditMode }}
                                    placeholder={`Example for Encompass Document Event`}
                                    error={!!subscriptionParamsError}
                                    helperText={subscriptionParamsError || "Enter a JSON object or array. See example for Encompass document event."}
                                />
                                {form.subscriptionId ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            Subscription is active (ID: {form.subscriptionId})
                                        </Alert>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={handleDeleteSubscription}
                                            sx={{ mt: 1 }}
                                            disabled={!isEditMode}
                                        >
                                            Delete Subscription
                                        </Button>
                                    </Box>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleSubscribeWebhook}
                                        sx={{ mt: 2 }}
                                        disabled={!form.config.SubscribeURL || !!subscriptionParamsError || !isEditMode}
                                    >
                                        Subscribe to Webhook
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                    {/*
                    {[1, 4].indexOf(selectedConnection?.type ?? 77) !== -1 && (
                        <>
                            <Box display="flex" alignItems="center" mb={1} gap={1} mt={1}>
                                <TextField
                                    select
                                    margin="dense"
                                    label="Method"
                                    name="method"
                                    value={form.config.method}
                                    onChange={handleChange}
                                    InputProps={{ readOnly: !isEditMode }}
                                    variant={isEditMode ? 'outlined' : 'filled'}
                                    sx={{ width: '250px' }}>
                                    <MenuItem value="GET">GET</MenuItem>
                                    <MenuItem value="POST">POST</MenuItem>
                                </TextField>

                                <TextField
                                    select
                                    margin="dense"
                                    label="Payload Type"
                                    name="PayloadType"
                                    value={form.config.PayloadType}
                                    InputProps={{ readOnly: !isEditMode }}
                                    variant={isEditMode ? 'outlined' : 'filled'}
                                    onChange={handleChange}
                                    sx={{ width: '250px', marginLeft: '5px' }}
                                >
                                    <MenuItem value="object">Object</MenuItem>
                                    <MenuItem value="array">Array</MenuItem>
                                </TextField>
                            </Box>
                            
                            <Box mt={2}>
                                <Typography variant="subtitle1">
                                    {form.config.method === 'POST' ? 'Body Params' : 'Query Params'}
                                </Typography>
                                {paramsArr.map((param, idx) => {
                                    const key = Object.keys(param)[0] || "";
                                    const value = param[key] || "";
                                    return (
                                        <Box key={idx} display="flex" alignItems="center" mb={1} gap={1}>
                                            <TextField
                                                label="Key"
                                                value={key}
                                                onChange={e => handleParamKeyChange(paramType, idx, e.target.value)}
                                                sx={{ width: '300px' }}
                                                InputProps={{ readOnly: !isEditMode }}
                                                variant={isEditMode ? 'outlined' : 'filled'}
                                            />
                                            <TextField
                                                label="Type"
                                                value={value}
                                                onChange={e => handleParamValueChange(paramType, idx, e.target.value)}
                                                select
                                                margin="dense"
                                                name="value"
                                                sx={{ width: '300px' }}
                                                InputProps={{ readOnly: !isEditMode }}
                                                variant={isEditMode ? 'outlined' : 'filled'}
                                            >
                                                {DataTypes.map((dt) => (
                                                    <MenuItem key={dt} value={dt}>{dt}</MenuItem>
                                                ))}
                                            </TextField>
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveParam(paramType, idx)}
                                                size="small"
                                                disabled={!isEditMode}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    );
                                })}
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleAddParam(paramType)}
                                    size="small"
                                    disabled={!isEditMode}
                                >
                                    Add Param
                                </Button>
                            </Box>

                        </>
                    )}*/}
                    {[1, 4].indexOf(selectedConnection?.type ?? 77) === -1 && (
                        <Alert severity="warning">This connection type is not yet supported to add parameters, Defaults generated</Alert>
                    )}
                    {/*<Box mt={2}>
                        <TextField
                            label="Config (JSON Preview)"
                            value={JSON.stringify(form.config, null, 2)}
                            fullWidth
                            multiline
                            minRows={4}
                            InputProps={{ readOnly: true }}
                            variant={isEditMode ? 'outlined' : 'filled'}
                        />
                    </Box>*/}
                </>
                //<Card sx={{ minWidth: 275, maxHeight: '85%', display: 'flex', flexDirection: 'column' }}>
                //    <CardContent
                //        sx={{
                //            flex: 1,
                //            overflowY: 'auto',
                //            maxHeight: '100%',
                //        }}
                //    >
                        
                //    </CardContent>
                //</Card>
            )
            }
               
        </Box>
    );
}
